import { buildTypedMutation, buildTypedQuery } from 'src/services/api.builder';
import { api } from 'src/services/api.service';

const programmingPlanApi = api.injectEndpoints({
  endpoints: (builder) => ({
    findProgrammingPlans: buildTypedQuery(builder, '/programming-plans', {
      providesTags: (result) => [
        { type: 'ProgrammingPlan', id: 'LIST' },
        ...(result ?? []).map(({ id }) => ({
          type: 'ProgrammingPlan' as const,
          id
        }))
      ]
    }),
    getProgrammingPlan: buildTypedQuery(
      builder,
      '/programming-plans/:programmingPlanId',
      {
        providesTags: (result) => [{ type: 'ProgrammingPlan', id: result?.id }]
      }
    ),
    createProgrammingPlan: buildTypedMutation(
      builder,
      '/programming-plans',
      'post',
      {
        invalidatesTags: () => [{ type: 'ProgrammingPlan', id: 'LIST' }]
      }
    ),
    updateProgrammingPlanStatus: buildTypedMutation(
      builder,
      '/programming-plans/:programmingPlanId',
      'put',
      {
        invalidatesTags: (_result, _error, { programmingPlanId }) => [
          { type: 'ProgrammingPlan', id: programmingPlanId },
          { type: 'ProgrammingPlan', id: 'LIST' }
        ]
      }
    ),
    findProgrammingPlanSettings: buildTypedQuery(
      builder,
      '/programming-plans/:programmingPlanId/settings',
      {
        providesTags: (_result, _error, { programmingPlanId }) => [
          { type: 'ProgrammingPlanSettings', id: programmingPlanId }
        ]
      }
    ),
    updateProgrammingPlanSettings: buildTypedMutation(
      builder,
      '/programming-plans/:programmingPlanId/settings',
      'put',
      {
        invalidatesTags: (_result, _error, { programmingPlanId }) => [
          'ProgrammingPlanSettings',
          'SpecificDataField',
          { type: 'ProgrammingPlan', id: programmingPlanId },
          { type: 'ProgrammingPlan', id: 'LIST' },
          { type: 'Document', id: 'LIST' }
        ]
      }
    ),
    findProgrammingSubPlanSettings: buildTypedQuery(
      builder,
      '/programming-plans/:programmingPlanId/sub-plans/:programmingSubPlanId/settings',
      {
        providesTags: (_result, _error, { programmingSubPlanId }) => [
          { type: 'ProgrammingPlanSettings', id: programmingSubPlanId }
        ]
      }
    ),
    updateProgrammingSubPlanSettings: buildTypedMutation(
      builder,
      '/programming-plans/:programmingPlanId/sub-plans/:programmingSubPlanId/settings',
      'put',
      {
        invalidatesTags: (
          _result,
          _error,
          { programmingPlanId, programmingSubPlanId }
        ) => [
          { type: 'ProgrammingPlanSettings', id: programmingSubPlanId },
          'SpecificDataField',
          { type: 'ProgrammingPlan', id: programmingPlanId },
          { type: 'ProgrammingPlan', id: 'LIST' }
        ]
      }
    ),
    duplicateProgrammingPlan: buildTypedMutation(
      builder,
      '/programming-plans/:programmingPlanId/duplicate',
      'post',
      {
        invalidatesTags: () => [{ type: 'ProgrammingPlan', id: 'LIST' }]
      }
    ),
    createProgrammingSubPlan: buildTypedMutation(
      builder,
      '/programming-plans/:programmingPlanId/sub-plans',
      'post',
      {
        invalidatesTags: (_result, _error, { programmingPlanId }) => [
          { type: 'ProgrammingPlan', id: programmingPlanId },
          { type: 'ProgrammingPlan', id: 'LIST' },
          { type: 'Prescription', id: 'LIST' },
          { type: 'LocalPrescription', id: 'LIST' }
        ]
      }
    ),
    duplicateProgrammingSubPlan: buildTypedMutation(
      builder,
      '/programming-plans/:programmingPlanId/sub-plans/:programmingSubPlanId/duplicate',
      'post',
      {
        invalidatesTags: (_result, _error, { programmingPlanId }) => [
          { type: 'ProgrammingPlan', id: programmingPlanId },
          { type: 'ProgrammingPlan', id: 'LIST' },
          { type: 'Prescription', id: 'LIST' },
          { type: 'LocalPrescription', id: 'LIST' }
        ]
      }
    ),
    deleteProgrammingPlan: buildTypedMutation(
      builder,
      '/programming-plans/:programmingPlanId',
      'delete',
      {
        invalidatesTags: (_result, _error, { programmingPlanId }) => [
          { type: 'ProgrammingPlan', id: programmingPlanId },
          { type: 'ProgrammingPlan', id: 'LIST' }
        ]
      }
    ),
    deleteProgrammingSubPlan: buildTypedMutation(
      builder,
      '/programming-plans/:programmingPlanId/sub-plans/:programmingSubPlanId',
      'delete',
      {
        invalidatesTags: (_result, _error, { programmingPlanId }) => [
          { type: 'ProgrammingPlan', id: programmingPlanId },
          { type: 'ProgrammingPlan', id: 'LIST' },
          { type: 'Prescription', id: 'LIST' },
          { type: 'LocalPrescription', id: 'LIST' }
        ]
      }
    ),
    updateProgrammingPlanLocalStatus: buildTypedMutation(
      builder,
      '/programming-plans/:programmingPlanId/local-status',
      'put',
      {
        invalidatesTags: (_result, _error, { programmingPlanId }) => [
          { type: 'ProgrammingPlan', id: programmingPlanId },
          { type: 'ProgrammingPlan', id: 'LIST' },
          { type: 'LocalPrescription', id: 'LIST' },
          { type: 'Prescription', id: 'LIST' }
        ]
      }
    ),
    sendProgrammingPlansToRegions: buildTypedMutation(
      builder,
      '/programming-plans/send-to-regions',
      'post',
      {
        invalidatesTags: (result) => [
          ...(result ?? []).map(({ id }) => ({
            type: 'ProgrammingPlan' as const,
            id
          })),
          { type: 'ProgrammingPlan', id: 'LIST' },
          { type: 'LocalPrescription', id: 'LIST' },
          { type: 'Prescription', id: 'LIST' }
        ]
      }
    ),
    launchProgrammingPlansCampaign: buildTypedMutation(
      builder,
      '/programming-plans/launch-campaign',
      'post',
      {
        invalidatesTags: (result) => [
          ...(result ?? []).map(({ id }) => ({
            type: 'ProgrammingPlan' as const,
            id
          })),
          { type: 'ProgrammingPlan', id: 'LIST' },
          { type: 'LocalPrescription', id: 'LIST' },
          { type: 'Prescription', id: 'LIST' }
        ]
      }
    ),
    sendProgrammingPlansToDepartments: buildTypedMutation(
      builder,
      '/programming-plans/send-to-departments',
      'post',
      {
        invalidatesTags: (result) => [
          ...(result ?? []).map(({ id }) => ({
            type: 'ProgrammingPlan' as const,
            id
          })),
          { type: 'ProgrammingPlan', id: 'LIST' },
          { type: 'LocalPrescription', id: 'LIST' },
          { type: 'Prescription', id: 'LIST' }
        ]
      }
    ),
    sendProgrammingPlansToSamplers: buildTypedMutation(
      builder,
      '/programming-plans/send-to-samplers',
      'post',
      {
        invalidatesTags: (result) => [
          ...(result ?? []).map(({ id }) => ({
            type: 'ProgrammingPlan' as const,
            id
          })),
          { type: 'ProgrammingPlan', id: 'LIST' },
          { type: 'LocalPrescription', id: 'LIST' },
          { type: 'Prescription', id: 'LIST' }
        ]
      }
    )
  })
});

export const useFindProgrammingPlansQuery: typeof programmingPlanApi.useFindProgrammingPlansQuery =
  programmingPlanApi.useFindProgrammingPlansQuery;
export const useGetProgrammingPlanQuery: typeof programmingPlanApi.useGetProgrammingPlanQuery =
  programmingPlanApi.useGetProgrammingPlanQuery;

export const {
  useCreateProgrammingPlanMutation,
  useCreateProgrammingSubPlanMutation,
  useUpdateProgrammingPlanStatusMutation,
  useUpdateProgrammingPlanLocalStatusMutation,
  useFindProgrammingPlanSettingsQuery,
  useUpdateProgrammingPlanSettingsMutation,
  useFindProgrammingSubPlanSettingsQuery,
  useUpdateProgrammingSubPlanSettingsMutation,
  useDeleteProgrammingPlanMutation,
  useDeleteProgrammingSubPlanMutation,
  useDuplicateProgrammingPlanMutation,
  useDuplicateProgrammingSubPlanMutation,
  useSendProgrammingPlansToRegionsMutation,
  useLaunchProgrammingPlansCampaignMutation,
  useSendProgrammingPlansToDepartmentsMutation,
  useSendProgrammingPlansToSamplersMutation
} = programmingPlanApi;
